import type { ConfigApi } from '@eis/insuranceservice-sdk';

/**
 * Reading a `group_core` asset.
 *
 * The asset crosses the wire as an untyped Struct, because JSON Schema is the contract for its
 * shape (backend standard §20) and a hand-mirrored protobuf message would be a second, drifting
 * copy of it. That decision is right at the service boundary and it means the console has to do
 * this reading itself — carefully, and with every field optional, because a group_core version
 * that adds a field must not blank the panel for the ones that do not have it yet.
 *
 * What the panel shows is what a group tarif ADDS to the child it pins (service summary §3.2):
 * the group factor, how child premiums aggregate into the master's obligation, and the billing
 * defaults a Group Policy inherits at creation. None of it is editable here — the asset is
 * published, and choosing which one a version pins is the operation this surface offers.
 */

export interface GroupFactor {
  readonly key: string;
  readonly type: string;
  readonly value: string;
  readonly appliedAt: string;
}

export interface GroupCoreSummary {
  readonly factors: readonly GroupFactor[];
  readonly aggregationMode: string;
  readonly proRata: string;
  readonly billingMode: string;
  readonly billingFrequency: string;
  readonly dunning: string;
  readonly conditionBundles: number;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const asText = (value: unknown): string =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';

export function summarizeGroupCore(asset: ConfigApi.CoreConfig | undefined): GroupCoreSummary {
  const config = asRecord(asset?.config);
  const aggregation = asRecord(config.aggregation);
  const billing = asRecord(config.billingDefaults);
  const factors: unknown = config.groupFactors;
  const bundles: unknown = config.conditionBundles;

  return {
    factors: (Array.isArray(factors) ? factors : []).map((entry) => {
      const factor = asRecord(entry);
      return {
        key: asText(factor.key),
        type: asText(factor.type),
        // A percentage a premium is multiplied by. Kept as the text it arrived as rather than
        // parsed into a number: this panel displays it, and the only place it is ever applied is
        // the service's rating engine.
        value: asText(factor.value),
        appliedAt: asText(factor.appliedAt),
      };
    }),
    aggregationMode: asText(aggregation.mode),
    proRata: asText(aggregation.proRata),
    billingMode: asText(billing.mode),
    billingFrequency: asText(billing.frequency),
    dunning: asText(billing.dunning),
    conditionBundles: Array.isArray(bundles) ? bundles.length : 0,
  };
}

/** The `group_core` ERN a manifest pins, read out of the manifest's refs block. */
export function pinnedGroupCoreErn(manifest: unknown): string {
  const refs = asRecord(asRecord(manifest).refs);
  return asText(refs.groupCore);
}

/** The exact physical child version a manifest pins (decision S-3). */
export function pinnedChildVersionErn(manifest: unknown): string {
  const refs = asRecord(asRecord(manifest).refs);
  return asText(refs.childTarifVersion);
}
